import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBlocker } from 'react-router-dom';

import { toast } from '@utilities';

import { DEFAULT_PROJECT_NAME } from '../components/NarrativesCanvas/utilities/constants';

const useDisposableProjectExit = ({
  activeChatSnapshot,
  chatSessionId,
  creatingChatSessionPromiseRef,
  deletedNarrativeIds,
  emptyChatHistory,
  flowId,
  isCreatingProject,
  onDeleteProject,
  optimisticNarratives,
  persistedNarratives,
  projectName,
}) => {
  const [pendingExit, setPendingExit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const hasNoChatHistory =
    emptyChatHistory ||
    (activeChatSnapshot?.wasHydrated && (activeChatSnapshot.messages?.length || 0) === 0);
  const hasRunNarratives = useMemo(() => {
    if (!chatSessionId) return false;
    if (flowId != null) return true;

    const deletedIds = new Set((deletedNarrativeIds || []).map(Number));
    const hasPersistedNarrative = (persistedNarratives || []).some(
      (narrative) => !deletedIds.has(Number(narrative?.id))
    );
    const hasOptimisticNarrative = (optimisticNarratives || []).some(
      (narrative) => !narrative?.isDeleted && !deletedIds.has(Number(narrative?.flowId))
    );

    return hasPersistedNarrative || hasOptimisticNarrative;
  }, [chatSessionId, deletedNarrativeIds, flowId, optimisticNarratives, persistedNarratives]);
  const shouldConfirmExit =
    (isCreatingProject && !chatSessionId && !flowId) ||
    (Boolean(chatSessionId) &&
      typeof projectName === 'string' &&
      projectName.trim() === DEFAULT_PROJECT_NAME &&
      hasNoChatHistory &&
      !hasRunNarratives);

  const requestExit = useCallback(
    (onContinue) => {
      if (!shouldConfirmExit) {
        onContinue?.();
        return true;
      }

      setPendingExit({ chatSessionId, onContinue });
      return false;
    },
    [chatSessionId, shouldConfirmExit]
  );

  const requestProjectOpen = useCallback(
    (nextSessionId) => {
      if (!shouldConfirmExit || !chatSessionId || chatSessionId === nextSessionId) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        setPendingExit({
          chatSessionId,
          onCancel: () => resolve(false),
          onContinue: () => resolve(true),
        });
      });
    },
    [chatSessionId, shouldConfirmExit]
  );

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        shouldConfirmExit && currentLocation.pathname !== nextLocation.pathname,
      [shouldConfirmExit]
    )
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    setPendingExit({ chatSessionId, onContinue: blocker.proceed });
  }, [blocker.proceed, blocker.state, chatSessionId]);

  const cancelExit = useCallback(() => {
    if (isDeleting) return;
    pendingExit?.onCancel?.();
    if (blocker.state === 'blocked') blocker.reset();
    setPendingExit(null);
  }, [blocker, isDeleting, pendingExit]);

  const confirmExit = useCallback(async () => {
    if (!pendingExit || isDeleting) return;

    setIsDeleting(true);
    try {
      const projectId = pendingExit.chatSessionId || (await creatingChatSessionPromiseRef.current);
      if (projectId) await onDeleteProject(projectId);
      pendingExit.onContinue?.();
      setPendingExit(null);
    } catch (error) {
      console.error('Failed to delete disposable project', error);
      toast.error('We could not delete this project. Please try again.');
      if (blocker.state === 'blocked') blocker.reset();
    } finally {
      setIsDeleting(false);
    }
  }, [blocker, creatingChatSessionPromiseRef, isDeleting, onDeleteProject, pendingExit]);

  return {
    cancelExit,
    confirmExit,
    isDeleting,
    isExitConfirmationOpen: Boolean(pendingExit),
    requestExit,
    requestProjectOpen,
  };
};

export default useDisposableProjectExit;
